import { McLeodClassicIntegration, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { AutogenLayoutBillingProfile } from "./autogen/AutogenLayoutBillingProfile";
import { EDITemplateSlideout, EDITemplateUpdateProps } from "./EDITemplateSlideout";
import {
    BlurEvent,
    Button,
    ChangeEvent,
    Checkbox,
    ClickEvent,
    CrudDecorator,
    CrudDecoratorCloseEvent,
    DataHeaderProps,
    DataSourceAction,
    DataSourceExecutionEvent,
    DataSourceMode,
    DataSourceValidationEvent,
    DropdownSelectionEvent,
    Layout,
    LookupModelSearchEvent,
    LookupModelSelectionEvent,
    SlideoutDecorator,
    Snackbar,
    Switch,
    TableRow,
    TableRowCreationEvent,
    TableRowDisplayEvent,
    TableRowMoveEvent,
    Textbox,
    ValidationResult
} from "@mcleod/components";
import { ModelEdiTemplate } from "./models/ModelEdiTemplate";
import { handleCommIdSelection, setQuickInfo } from "./DataFusionUtil";
import { createCustomizationSlideout, handleButtonMasterBatchClick, handleMasterBatchCheck, isIntercompanyEnabled, setFuncAckTemplateValues, setTemplateValues } from "./EdiUtility";
import { LMECompanySettings } from "@mcleod/general";
import { EdiRequiredField } from "./EdiRequiredField";
import { CrudDecoratorProps } from "@mcleod/components/src/page/decorators/CrudDecoratorProps";
import { EdiImagingConfigSlideout } from "./EdiImagingConfigSlideout";
import { TransmissionSchedule } from "./TransmissionSchedule";
import { EdiCustomTransControl } from "./EdiCustomTransControl";
import { EdiShipMatchCritBilling } from "./EdiShipMatchCritBilling";
import { CommonDialogs } from "@mcleod/common";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { ModelEdiCustomer } from "./models/ModelEdiCustomer";
import { ModelEdibillingProfile } from "./models/ModelEdibillingProfile";

const layoutURL = "lme/datafusion/BillingProfile";

export class BillingProfile extends AutogenLayoutBillingProfile {
    private _isBrokerageLtlCompany: boolean = false;
    private _isAssetLtl: boolean = false;
    private _isIcEnabled: boolean = false;
    private static readonly MINUTES_PER_HOUR = 60;
    private static readonly INTERVAL_HOURS_FIELD = "audit_interval_hours";
    private static readonly INTERVAL_MINUTES_FIELD = "audit_interval_minutes";

    private readonly TRANSACTION_TYPE_210 = "210";

    private INBOUND = "I";
    private OUTBOUND = "O";
    private BILLING = "I";
    private FUNCTIONAL_ACK = "F";

    override async onLoad() {
        this._isIcEnabled = await isIntercompanyEnabled();
        this.checkboxIntercompany.visible = this._isIcEnabled;
        this.setupTableRequiredFieldHandlers();
        this.setupTableImagingHandlers();
        this.tableShipMatchCrit.crudManager.showCrud = (mode: DataSourceMode, props?: Partial<CrudDecoratorProps>, dataHeaderProps?: Partial<DataHeaderProps>) => {
            const layoutName = "lme/datafusion/EdiShipMatchCritBilling";
            const layout = Layout.getLayout(layoutName) as EdiShipMatchCritBilling;

            // Safely assign values with null checks
            const activeRow = this.mainDataSource?.activeRow;
            if (activeRow) {
                layout.profileId = activeRow.get("id");
                layout.version = activeRow.get("version");
                layout.partnerId = activeRow.get("partner_id");
            }
            const crudDecorator = new CrudDecorator({
                layout: layout,
                mode: mode,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                    title: "Add Shipment Matching Criteria",
                    doAfterClose: (event: CrudDecoratorCloseEvent) => (event) => { },
                    ...dataHeaderProps,
                },
                doAfterSlideOut: (decorator) => {
                    // Resequence all rows after any CRUD operation
                    this.tableShipMatchCrit.allRows.forEach((row, index) => {
                        const textboxSequence = row.findComponentById("textboxShipMatchSequence") as Textbox;
                        if (textboxSequence) {
                            textboxSequence.text = (index + 1).toString();
                        }
                        row.data.set("sequence", index + 1);
                    });
                },
                ...props
            });
            crudDecorator.slideIn({ speed: 200 });
        };

        this.tableShipMatchCrit.addButtonCaption = "Add";
        this.mainDataSource.doBeforeDataSourcePost = async (): Promise<boolean> => {
            if (this.mainDataSource?.mode === DataSourceMode.ADD &&
                this.mainDataSource?.activeRow?.get("partner_id") &&
                this.mainDataSource?.activeRow?.get("alt_partner_id") &&
                this.mainDataSource?.activeRow?.get("version") &&
                this.mainDataSource?.activeRow?.get("company_id") &&
                this.mainDataSource?.activeRow?.get("direction")) {

                const validationResults: ValidationResult[] = this.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                    return false;
                }

                const partnerId = this.mainDataSource?.activeRow?.get("partner_id");
                const altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
                const version = this.mainDataSource?.activeRow?.get("version");
                const companyId = this.mainDataSource?.activeRow?.get("company_id");
                const direction = this.mainDataSource?.activeRow?.get("direction");
                const transactionSet = this.mainDataSource?.activeRow?.get("transaction_set", this.TRANSACTION_TYPE_210);
                if (await this.profileExists(partnerId, altPartnerId, version, companyId, direction, transactionSet)) {
                    CommonDialogs.showError("Partner profile already exists");
                    return false;
                }
            }
            return true;
        };
    }

    async afterExecution(event: DataSourceExecutionEvent) {
        this.tabsetBillingProfile.visible = this.mainDataSource?.mode !== DataSourceMode.ADD;
        const direction = this.mainDataSource?.activeRow?.get("direction");
        this.setVisibilityByDirection(direction);
        this.updateUIVisibility(direction);
        this.setAuditRequiredFields(direction);
        this.setNextBatchFields();
        if (event && event.getAction() === DataSourceAction.UPDATE && this.mainDataSource?.activeRow?.get("template_id")) {
            this.buttonSetup.enabled = true;
        }
        this.buttonFuncAckTemplate.enabled = this.mainDataSource?.activeRow?.get("func_ack_tid") && this.mainDataSource.mode !== DataSourceMode.ADD;
        this.setCompanyField(this.mainDataSource?.activeRow?.getBoolean("intercompany") && this._isIcEnabled);
        if (this.OUTBOUND === direction) {
            await this.layoutInboundFuncAck.mainDataSource.search({
                alt_partner_id: this.mainDataSource?.activeRow?.get("alt_partner_id"),
                partner_id: this.mainDataSource?.activeRow?.get("partner_id"),
                version: this.mainDataSource?.activeRow?.get("version")
            });
        }
        if (this.INBOUND === direction) {
            const autoAccept = this.mainDataSource?.activeRow?.get("auto_accept");
            this.textboxErrorOptions.visible = autoAccept !== "N";
        }
        this.textboxEdiCommId.required = this.OUTBOUND === direction && !this.checkboxIntercompany.checked;
        this.buttonFuncAckTemplate.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
    }

    public static navigateTo(recordId: string) {
        const url = layoutURL + `?mode=update&key=${recordId}`;
        Navigation.navigateTo(url, { newTab: true })
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.edi.EntryEdiBillingProfile", "<ext> search id ='" + this.mainDataSource?.activeRow?.get("id") + "'");
    }

    doBeforeModeChange() {
        this.setTitle();
    }

    doAfterModeChange() {
        this.setTitle();
    }

    setupButtonOnClick() {
        this.setupOnClick("template_id", (returnedValues) => {
            this.updateFromTemplate(returnedValues);
        });
    }

    updateFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxTemplateId.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("template_id"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("template_id", lookupModelRow);
        this.textboxTemplateId.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("version", updatedValues.version);
        this.mainDataSource?.activeRow?.set("transaction_set", updatedValues.transactionSet);
        if (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND) {
            this.mainDataSource?.activeRow?.set("partner_id", updatedValues.appSenderId);
            this.mainDataSource?.activeRow?.set("alt_partner_id", updatedValues.senderId);
        } else {
            this.mainDataSource?.activeRow?.set("partner_id", updatedValues.appReceiverId);
            this.mainDataSource?.activeRow?.set("alt_partner_id", updatedValues.receiverId);
        }
    }

    onChangeIntercompany(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.setCompanyField(event.newValue);
        }
    }

    templateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.BILLING, this.mainDataSource?.activeRow?.get("direction"));
    }

    communicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.BILLING);
    }

    setCompanySettings() {
        const companySettings = LMECompanySettings.getSingleton();
        this.isAssetLtl = companySettings.isAssetLtl();
        this.isBrokerageLtlCompany = companySettings.isBrokerageLtl();
        const isLtl = this.isAssetLtl || this.isBrokerageLtlCompany;
        this.layoutHandlingRequirementXref.visible = isLtl;
        this.layoutPackageTypeXref.visible = isLtl;
    }

    /**
         * Sets up event handlers and configurations for the tableRequiredField component
         * including edit layout loading, row creation listeners, and CRUD manager setup
         */
    private setupTableRequiredFieldHandlers() {
        this.tableRequiredField.onEditLayoutLoaded = (decorator, tableRow) => {
            const reqRulesLayout = decorator.layout as EdiRequiredField;
            reqRulesLayout.transactionType = this.TRANSACTION_TYPE_210;
            reqRulesLayout.isGroupRule = false;
            decorator.labelTitle.text = "Invoice Data Validation Rules";

            const superOnSave = decorator.onSave;
            decorator.onSave = (updatedRow) => {
                if (decorator.layout.mainDataSource.validate()) {
                    const rules = reqRulesLayout.tableEdiDataRule?.data;
                    updatedRow.set('data_rules', rules);
                    superOnSave(updatedRow);
                }
            };
        };

        // Set up event handlers for when table rows are created or displayed
        this.tableRequiredField.addRowCreateListener((event) => {
            // This fires when a new row is being added to the table
            const tableRow = event.getTableRow();
            if (tableRow.table.editLayout) {
                // Override the edit layout loading process
                const originalEditLayoutLoaded = tableRow.table.onEditLayoutLoaded;
                tableRow.table.onEditLayoutLoaded = (decorator, row) => {
                    const layout = decorator.layout;
                    if (layout instanceof EdiRequiredField) {
                        // Set the transaction type to "R" for Reply when opened from LoadTenderReplyProfile
                        layout.transactionType = this.TRANSACTION_TYPE_210;
                        layout.version = this.mainDataSource.activeRow?.get("version");
                        layout.isGroupRule = false;
                        // You can set other variables here as needed
                        // For example, you could pass partner information:
                        // layout.someOtherProperty = this.mainDataSource.activeRow.get("some_field");
                    }

                    // Call the original handler if it exists
                    if (originalEditLayoutLoaded) {
                        originalEditLayoutLoaded(decorator, row);
                    }
                };
            }
        });
        this.tableRequiredField.crudManager.showCrud = (mode: DataSourceMode, props?: Partial<CrudDecoratorProps>, dataHeaderProps?: Partial<DataHeaderProps>) => {
            // Force the open type to be SLIDEOUT
            const layoutName = "lme/datafusion/EdiRequiredField";
            if (layoutName == null) {
                return;
            }
            const layout = Layout.getLayout(layoutName) as EdiRequiredField;
            layout.version = this.mainDataSource.activeRow?.get("version");
            layout.transactionType = this.TRANSACTION_TYPE_210;
            layout.isGroupRule = false;
            const crudDecorator = new CrudDecorator({
                layout: layout,
                mode: mode,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                    title: "Invoice Data Validation Rules",
                    doAfterClose: (event: CrudDecoratorCloseEvent) => (event) => { },
                    ...dataHeaderProps,
                },
                ...props
            });
            crudDecorator.slideIn({ speed: 200 });
        };

        // Customize the add button text to just say "Add"
        this.tableRequiredField.addButtonCaption = "Add";
    }

    private setupTableImagingHandlers() {
        this.tableImaging.addButtonCaption = "Add";
        this.tableImaging.onEditLayoutLoaded = (decorator, tableRow) => {
            decorator.multiButton.dropdownItems = null;
            const layout = decorator.layout as EdiImagingConfigSlideout;
            layout.parentId = this.mainDataSource?.activeRow?.get("id");
            layout.currentIds = this.tableImaging.data.map(row => {
                const id = row.get("image_type").toString();
                if (row.get("id") !== tableRow.data.get("id")) return id;
            });
        };
        this.tableImaging.crudManager.showCrud = (mode: DataSourceMode, props?: Partial<CrudDecoratorProps>, dataHeaderProps?: Partial<DataHeaderProps>) => {
            // Force the open type to be SLIDEOUT
            const layoutName = "lme/datafusion/EdiImagingConfigSlideout";
            if (layoutName == null) {
                return;
            }
            const layout = Layout.getLayout(layoutName) as EdiImagingConfigSlideout;
            layout.parentId = this.mainDataSource?.activeRow?.get("id");
            layout.currentIds = this.tableImaging.data.map(row => row.get("image_type").toString());
            const crudDecorator = new CrudDecorator({
                layout: layout,
                mode: mode,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                    doAfterClose: (event: CrudDecoratorCloseEvent) => (event) => { },
                    ...dataHeaderProps,
                },
                ...props
            });
            crudDecorator.slideIn({ speed: 200 });
        };
    }

    async templateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                setTemplateValues(template, this.mainDataSource);
            }
        }
    }

    /** This is an event handler for the onRowCreate event of tableEdiCustomer. */
    tableEdiCustomerOnRowCreate(event: TableRowCreationEvent) {
        const row = event.target as TableRow;
        const newRow = event.getTableRow().data;
        // Reset original data FIRST, before setting values, to prevent marking datasource as changed
        newRow.resetOriginalData();
        row.data.set("alt_partner_id", this.mainDataSource?.activeRow?.get("alt_partner_id"));
        row.data.set("partner_id", this.mainDataSource?.activeRow?.get("partner_id"));
        row.data.set("version", this.mainDataSource?.activeRow?.get("version"));
        row.data.set("direction", this.mainDataSource?.activeRow?.get("direction"));
        row.data.set("transaction_set", this.mainDataSource?.activeRow?.get("transaction_set"));
        row.data.set("partner_name", this.mainDataSource?.activeRow?.get("partner_name"));
        row.data.set("transaction_type", this.BILLING);
        // Reset again after setting to clear the dirty flag
        newRow.resetOriginalData();
        // Force the datasource to recalculate its hasChanged state
        this.sourceEdiCommodity?.notifyHasChangedComponents(false);
    }

    /** This is an event handler for the onRowCreate event of tableRequiredField. */
    tableRequiredFieldOnRowCreate(event: TableRowCreationEvent) {
        const partnerId = this.mainDataSource?.activeRow?.get("partner_id");
        const altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
        const version = this.mainDataSource?.activeRow?.get("version");
        const transactionSet = this.mainDataSource?.activeRow?.get("transaction_set");
        const direction = this.mainDataSource?.activeRow?.get("direction");
        const row = event.target as TableRow;
        row.data.set("partner_id", partnerId);
        row.data.set("alt_partner_id", altPartnerId);
        row.data.set("version", version);
        row.data.set("transaction_set", transactionSet);
        row.data.set("direction", direction);
    }

    tableEdiCustomerOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.target as TableRow;
        const modelRow = row.data;
        if (modelRow) {
            const isRequired = modelRow.getBoolean("require_correction_ind");
            const defaultCorrectIndicator = row.findComponentById("textboxDefaultCorrectionInd") as Textbox;
            if (defaultCorrectIndicator) {
                defaultCorrectIndicator.enabled = isRequired;
            }
        }
        const textboxCustomerId = row.findComponentById("textboxCustomerId") as Textbox;
        if (textboxCustomerId) {
            textboxCustomerId.addlValidationCallback = () => {
                const result: ValidationResult = { component: textboxCustomerId, isValid: true };
                const customerId = row.data?.get("customer_id");
                if (customerId) {
                    const duplicate = this.tableEdiCustomer.data.find(
                        existingRow => existingRow.get("customer_id") === customerId && existingRow !== row.data
                    );
                    if (duplicate) {
                        result.isValid = false;
                        result.validationMessage = "This customer has already been added to this profile.";
                    }
                }
                return result;
            };
        }
    }

    async textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!event.hasSelection()) return;
        const customerId = event.getValueFromSelectedRow("id");
        if (!customerId) return;
        const existingRecord = await new ModelEdiCustomer().searchSingle({
            customer_id: customerId,
            direction: this.OUTBOUND,
            transaction_type: this.BILLING
        });
        if (existingRecord) {
            const partnerName = existingRecord.get("partner_name") || existingRecord.get("alt_partner_id");
            CommonDialogs.showError(`This customer is already assigned to outbound billing profile: ${partnerName}`);
            const textbox = event.target as Textbox;
            const tableRow = TableRow.getContainingTableRow(textbox);
            if (tableRow) {
                tableRow.data.set("customer_id", null);
                tableRow.data.setLookupModelData("customer_id", null);
                textbox.text = "";
            }
        }
    }

    requireIndicatorOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const switchReqInd = event.target as Switch;
            const isRequired = event.newValue;
            const tableRow = TableRow.getContainingTableRow(switchReqInd);
            const defaultCorrectIndicator = tableRow.findComponentById("textboxDefaultCorrectionInd") as Textbox;
            if (defaultCorrectIndicator) {
                defaultCorrectIndicator.enabled = isRequired;
                if (!isRequired) {
                    tableRow.data.set("default_correction_ind", "");
                }
            }
        }
    }

    intervalHoursOnBlur(event: BlurEvent) {
        this.intervalValueOnBlur(event, true);
    }

    intervalMinutesOnBlur(event: BlurEvent) {
        this.intervalValueOnBlur(event, false);
    }

    buttonCustomFilenameOnClick() {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Invoice Filename",
            this.mainDataSource,
            "filename",
            "fileMask",
            false,
            this.mainDataSource?.activeRow?.get("next_batch") ?? 0
        );
    }

    buttonCustomizeTransControNoOnClick() {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomTransControl",
            "Customize Invoice Transmission Control Number",
            this.mainDataSource,
            "st02_format",
            "mask",
            false,
            this.mainDataSource?.activeRow?.get("next_batch") ?? 0
        );
    }

    buttonCustomizeImageFileNameOnClick() {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Invoice Zip Filename",
            this.mainDataSource,
            "zip_filename",
            "fileMask",
            true,
            this.mainDataSource?.activeRow?.get("next_batch") ?? 0
        );
    }

    imagingTableOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.target as TableRow;
        const modelRow = row.data;
        const textboxAltImgComm = row.findComponentById("textboxAltImgComm") as Textbox;
        if (textboxAltImgComm) {
            const altImgCommId = modelRow.get("alt_img_comm");
            if (altImgCommId) {
                setQuickInfo(altImgCommId, textboxAltImgComm, "lme/datafusion/EdiCommControlQuickInfo");
            }
        }

    }

    imagingTableOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data;
        modelRow.set("parent_id", this.mainDataSource?.activeRow?.get("id"));
        modelRow.set("company_id", this.mainDataSource?.activeRow?.get("company_id"));
    }

        /** This is an event handler for the onChange event of checkboxIndividualBatch. */
    checkboxIndividualBatchOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && this.checkboxIndividualBatch.checked && this.checkboxUseMBatch.checked) {
            this.checkboxIndividualBatch.checked = false;
            Snackbar.showWarningSnackbar("Individual Batching and Use Master Batch cannot both be selected.");
        }
    }

    async checkboxMasterBatchOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && this.checkboxUseMBatch.checked && this.checkboxIndividualBatch.checked) {
            this.checkboxUseMBatch.checked = false;
            Snackbar.showWarningSnackbar("Individual Batching and Use Master Batch cannot both be selected.");
            return;
        }
        await this.masterBatchOnChange(event);
    }

    /** This is an event handler for the onClick event of buttonMasterBatch. */
    async buttonMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
        const version = this.mainDataSource.activeRow.get("version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxUseMBatch.checked = true;
        });
    }

    /** This is an event handler for the onClick event of buttonSchedule. */
    buttonScheduleOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/datafusion/TransmissionSchedule") as TransmissionSchedule;
        const saveButton = new Button({
            caption: "Save & Close",
            rowBreak: false,
            color: "primary.reverse",
            backgroundColor: "primary",
            onClick: async (event: ClickEvent) => {
                saveButton.busy = true;
                layout.sourceEdischedule.post().then(() => {
                    saveButton.busy = false;
                    layout.slideOut();
                });

            }
        });
        const sod = new SlideoutDecorator({
            layout: layout,
            title: "Invoice Transmission Schedule",
            width: '75%',
            addlComponents: [saveButton],
            fillVerticalSpace: true,
            doAfterSlideIn: async () => {
                layout.parentRowID = this.mainDataSource.activeRow.get("id");
                layout.altPartnerID = this.mainDataSource.activeRow.get("alt_partner_id");
                layout.version = this.mainDataSource.activeRow.get("version");
                layout.partnerId = this.mainDataSource.activeRow.get("partner_id");
                layout.transactionType = "I";
                layout.transactionSet = "210";
                layout.parentRowType = "P";
                layout.mainDataSource.search({ parent_row_id: this.mainDataSource.activeRow.get("id") });

            }

        });
    }

    public get isBrokerageLtlCompany(): boolean {
        return this._isBrokerageLtlCompany;
    }
    public set isBrokerageLtlCompany(value: boolean) {
        this._isBrokerageLtlCompany = value;
    }
    public get isAssetLtl(): boolean {
        return this._isAssetLtl;
    }
    public set isAssetLtl(value: boolean) {
        this._isAssetLtl = value;
    }

    private updateUIVisibility(direction: string) {
        const isInbound = direction === this.INBOUND;

        this.layoutNotifications.tableEdiNotifyInbound.visible = isInbound;
        this.layoutNotifications.tableEdiNotifyOutbound.visible = !isInbound;
        this.layoutEdiApiUsers.setValues(this.BILLING, this.mainDataSource?.activeRow?.get("id"));
        this.layoutEdiPartnerQualifiers.setPartnerValues(this.mainDataSource?.activeRow?.get("partner_id"), this.mainDataSource?.activeRow?.get("version"));
        this.layoutEdiPartnerQualifiers.setColumnsForTender();
        this.buttonSchedule.visible = !isInbound;
        this.setCompanySettings();
    }

    private onLookupModelSearch(event: LookupModelSelectionEvent, textbox: Textbox, layout: string) {
        if (event.hasSelection()) {
            if (event.getValueFromSelectedRow("id")) {
                setQuickInfo(event.getValueFromSelectedRow("id"), textbox, layout);
            }
        }
    }

    private templateBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string, direction: string) {
        event.filter.direction = direction;
        event.filter.transaction_type = type;
    }

    private commBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string) {
        event.filter.direction_input = this.mainDataSource?.activeRow?.get("direction");
        event.filter.type = type;
    }

    defaultCorrectionIndBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.version_input = this.mainDataSource?.activeRow?.get("version");
        event.filter.element_input = "202";
    }

    async onValidate(event: DataSourceValidationEvent) {
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND && this.checkboxIntercompany.checked) {
            this.mainDataSource?.activeRow?.set("transmit_method", "X");
        }
        const commId = this.mainDataSource?.activeRow?.get("edi_comm_id");
        if (commId) {
            const matchingCommIds = this.tableImaging.data.some(row => row.get("alt_img_comm") === commId);
            if (matchingCommIds) {
                event.validationResults.push({
                    isValid: false,
                    validationMessage: "Alt Comm ID from the images tab has a duplicate comm id to the Communications ID at the profile level which will cause duplicate invoices to be transmitted.",
                    caption: "Alt Comm ID from the images tab has a duplicate comm id to the Communications ID at the profile level which will cause duplicate invoices to be transmitted.",
                    component: this.tableImaging
                });
            }
        }
    }

    private setCompanyField(isEnabled: boolean) {
        this.textboxSendToCompany.visible = isEnabled;
        this.textboxEdiCommId.visible = !isEnabled;
        const isRequired = !isEnabled && this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND;
        this.textboxEdiCommId.required = isRequired;
    }

    private setupOnClick(fieldName: string, callback: (returnedValues: EDITemplateUpdateProps) => void) {
        EDITemplateSlideout.showSlideout({
            templateId: this.mainDataSource?.activeRow?.get(fieldName), doAfterSlideOut:
                (returnedValues: EDITemplateUpdateProps) => {
                    callback(returnedValues);
                }
        });
    }

    private setVisibilityByDirection(direction: string) {
        this.panelInboundFuncAck.visible = direction === this.OUTBOUND;
        this.panelOutboundAck.visible = direction === this.INBOUND;
        this.panelOutboundGeneral.visible = direction === this.OUTBOUND;
        this.panelInboundGeneral.visible = direction === this.INBOUND;
        this.tabBillTo.visible = direction === this.OUTBOUND;
        this.tabImaging.visible = direction === this.OUTBOUND;
        this.tabDataValidation.visible = direction === this.OUTBOUND;
        this.tabCarriers.visible = direction !== this.OUTBOUND;
        this.tabCompare.visible = direction !== this.OUTBOUND;
        this.tabShipmentMatching.visible = direction !== this.OUTBOUND;
        this.layoutTrailerTypeXref.visible = direction === this.OUTBOUND;
        this.layoutChargeCodeXref.visible = direction === this.OUTBOUND;
        this.layoutDeductCodeXref.visible = direction !== this.OUTBOUND;
        this.labelFor.visible = direction === this.INBOUND;
        this.textboxAuditCutoffDays.visible = direction === this.INBOUND;
        this.labelDays.visible = direction === this.INBOUND;
    }

    private intervalValueOnBlur(event: BlurEvent, isHours: boolean) {
        if (event.changedWhileFocused) {
            const textbox = event.target as Textbox;
            if (StringUtil.isEmptyString(textbox.text)) {
                if (isHours) {
                    this.mainDataSource?.activeRow?.set(BillingProfile.INTERVAL_HOURS_FIELD, 0);
                } else {
                    this.mainDataSource?.activeRow?.set(BillingProfile.INTERVAL_MINUTES_FIELD, 0);
                }
            }
            this.updateAuditInterval(
                this.mainDataSource?.activeRow?.getNumber(BillingProfile.INTERVAL_HOURS_FIELD) ?? 0,
                this.mainDataSource?.activeRow?.getNumber(BillingProfile.INTERVAL_MINUTES_FIELD) ?? 0
            );
        }
    }

    updateAuditInterval(hoursValueInput: number, minutesValueInput: number) {
        const totalMinutes = hoursValueInput * BillingProfile.MINUTES_PER_HOUR + minutesValueInput;
        this.mainDataSource?.activeRow?.set("audit_interval", totalMinutes);
        let intervalHours = hoursValueInput;
        let intervalMinutes = minutesValueInput;
        if (minutesValueInput >= BillingProfile.MINUTES_PER_HOUR) {
            intervalHours += Math.floor(minutesValueInput / BillingProfile.MINUTES_PER_HOUR);
            intervalMinutes = minutesValueInput % BillingProfile.MINUTES_PER_HOUR;
        }
        this.mainDataSource?.activeRow?.set(BillingProfile.INTERVAL_HOURS_FIELD, intervalHours);
        this.mainDataSource?.activeRow?.set(BillingProfile.INTERVAL_MINUTES_FIELD, intervalMinutes);
    }

    /** This is an event handler for the onRowCreate event of tableEdiCarrier. */
    tableEdiCarrierOnRowCreate(event: TableRowCreationEvent) {
        const row = event.target as TableRow;
        const newRow = event.getTableRow().data;
        // Reset original data FIRST, before setting values, to prevent marking datasource as changed
        newRow.resetOriginalData();
        row.data.set("alt_partner_id", this.mainDataSource?.activeRow?.get("alt_partner_id"));
        row.data.set("partner_id", this.mainDataSource?.activeRow?.get("partner_id"));
        row.data.set("version", this.mainDataSource?.activeRow?.get("version"));
        row.data.set("direction", this.mainDataSource?.activeRow?.get("direction"));
        row.data.set("transaction_set", this.mainDataSource?.activeRow?.get("transaction_set"));
        row.data.set("transaction_type", "I");
        newRow.resetOriginalData();
        // Force the datasource to recalculate its hasChanged state
        this.sourceEdiCommodity?.notifyHasChangedComponents(false);
    }

    setupFuncAckButtonOnClick() {
        this.setupOnClick("func_ack_tid", (returnedValues) => {
            this.updateFuncAckFromTemplate(returnedValues);
        });
    }

    updateFuncAckFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxFuncAckTid.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("func_ack_tid"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("func_ack_tid", lookupModelRow);
        this.textboxFuncAckTid.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("func_version", updatedValues.version);
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND) {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appSenderId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.senderId);
        } else {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appReceiverId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.receiverId);
        }
    }

    async masterBatchFuncCheckboxOnChange(event: ChangeEvent) {
        await this.masterBatchOnChange(event);
    }

    private setNextBatchFields() {
        if (!this.checkboxUseFuncMasterBatch.checked) {
            this.textboxFuncNextBatch.visible = true;
            this.labelNextBatchNumber.visible = false;
            this.labelFuncMasterBatch.visible = false;
        } else {
            this.textboxFuncNextBatch.visible = false;
            this.labelNextBatchNumber.visible = true;
            this.labelFuncMasterBatch.visible = true;
        }
    }

    /** This is an event handler for the onClick event of buttonAckFilenameCustomize. */
    buttonAckFilenameCustomizeOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Functional Acknowledgement Filename",
            this.mainDataSource,
            "func_ack_filename",
            "fileMask",
            false,
            this.mainDataSource?.activeRow?.get("func_next_batch") ?? 0
        );
    }

    /** This is an event handler for the onClick event of buttonAckTransactionCustomize. */
    buttonAckTransactionCustomizeOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomTransControl",
            "Customize Functional Acknowledgement Transmission Control Number",
            this.mainDataSource,
            "func_ack_st02_frmt",
            "mask",
            false,
            this.mainDataSource?.activeRow?.get("func_next_batch") ?? 0
        );
    }

    funcAckTemplateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK, this.OUTBOUND);
    }

    async funcAckTemplateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        this.buttonFuncAckTemplate.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                setFuncAckTemplateValues(template, this.mainDataSource);
            }
        }
    }

    funcAckCommunicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK);
    }

    textboxAutoAcceptAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            const value = event.selection.value;
            this.textboxErrorOptions.visible = value !== "N";
        }
    }

    /** This is an event handler for the afterTableRowMove event of tableShipMatchCrit. */
    tableShipMatchCritAfterTableRowMove(event: TableRowMoveEvent) {
        // Only update sequences when rows are actually moved, not added
        this.tableShipMatchCrit.allRows.forEach((row, index) => {
            row.data.set("sequence", index + 1);
        });
        this.mainDataSource.mode = DataSourceMode.UPDATE;
    }

    /** This is an event handler for the onRowCreate event of tableShipMatchCrit. */
    tableShipMatchCritOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.getTableRow() as TableRow;

        if (tableRow.table.editLayout) {
            // Override the edit layout loading process
            const originalEditLayoutLoaded = tableRow.table.onEditLayoutLoaded;
            tableRow.table.onEditLayoutLoaded = (decorator, row) => {
                const criteriaLayout = decorator.layout as EdiShipMatchCritBilling;
                criteriaLayout.version = this.mainDataSource?.activeRow?.get("version");
                criteriaLayout.profileId = this.mainDataSource?.activeRow?.get("id");
                criteriaLayout.partnerId = this.mainDataSource?.activeRow?.get("partner_id");
                criteriaLayout.sourceEdiShipmatchCrit.activeRow?.set("version", this.mainDataSource?.activeRow?.get("version"));
                const layout = decorator.layout;
                if (criteriaLayout.sourceEdiShipmatchCrit.activeRow._appending) {
                    criteriaLayout.sourceEdiShipmatchCrit.activeRow.set("profile_id", this.mainDataSource?.activeRow?.get("id"));
                    // Don't set sequence here - let it be handled by doAfterSlideOut and onRowDisplay
                }
                // Hide Save & Add Another option in the multibutton for edit layout
                if (decorator.multiButton && decorator.multiButton.components && decorator.multiButton.components.length > 1) {
                    decorator.multiButton.components[1].visible = false;
                }

                // Call the original handler if it exists
                if (originalEditLayoutLoaded) {
                    originalEditLayoutLoaded(decorator, row);
                }
                const superOnSave = decorator.onSave;
                decorator.onSave = (updatedRow: any) => {
                    if (decorator.layout.mainDataSource.validate()) {
                        const rules = criteriaLayout.tableShipMatchRule?.data;
                        updatedRow.set('shipment_match_rules', rules);

                        // Update sequence for the newly created row after save
                        setTimeout(() => {
                            const newRow = this.tableShipMatchCrit.allRows.find(r => r.data === updatedRow);
                            if (newRow) {
                                this.updateShipMatchCritRowSequence(newRow);
                            }
                        }, 0);

                        superOnSave(updatedRow);
                    }
                };
            };
        }
    }

    /** This is an event handler for the onRowDisplay event of tableShipMatchCrit. */
    tableShipMatchCritOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.getTableRow() as TableRow;
        this.updateShipMatchCritRowSequence(row);
    }

    /**
     * Updates the sequence for a single shipment match criteria row based on its position
     * @param row The table row to update
     */
    private updateShipMatchCritRowSequence(row: TableRow) {
        // Set the sequence based on the row's position in the table (1-based)
        const allRows = this.tableShipMatchCrit.allRows;
        const rowIndex = allRows.indexOf(row);
        if (rowIndex >= 0) {
            const sequenceValue = rowIndex + 1;
            row.data.set("sequence", sequenceValue);

            // Update any sequence textbox in the row if it exists
            const sequenceTextbox = row.findComponentById("textboxSequence") as Textbox;
            if (sequenceTextbox) {
                sequenceTextbox.text = sequenceValue.toString();
            }
        }
    }

    setTitle() {
        this.dataHeader.title = (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND ? "Inbound " : "Outbound ") + "Invoice Profile";
    }

    /** This is an event handler for the onClick event of buttonFuncAckMasterBatch. */
    async buttonFuncAckMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
        const version = this.mainDataSource.activeRow.get("version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxUseFuncMasterBatch.checked = true;
        });
    }

    private async masterBatchOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const check = event.target as Checkbox;
            const partnerName = this.mainDataSource.activeRow.get("partner_name");
            const partnerID = this.mainDataSource.activeRow.get("partner_id");
            const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
            const version = this.mainDataSource.activeRow.get("version");

            await handleMasterBatchCheck(check, partnerName, partnerID, altPartnerId, version);
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxEdiCommId. */
    textboxEdiCommIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const field = textbox.field;
        if (this.mainDataSource?.activeRow.get("direction") === "O") {
            handleCommIdSelection(field, textbox, event.hasSelection(), this.mainDataSource?.activeRow, "210");
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxFuncCommId. */
    textboxFuncCommIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const field = textbox.field;
        handleCommIdSelection(field, textbox, event.hasSelection(), this.mainDataSource?.activeRow, "997");
    }

    textboxCarrierIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const carrierTextBox = tableRow?.findComponentById("textboxCarrierId") as Textbox;
        event.filter.status = "A";
        if (carrierTextBox) {
            event.filter.lm_search = carrierTextBox.text;
        }
    }

    /** This is an event handler for the onChange event of textboxTemplateId. */
    textboxTemplateIdOnChange(event: ChangeEvent) {
        this.buttonSetup.enabled = !StringUtil.isEmptyString(this.textboxTemplateId.text);
    }

    async profileExists(profileId: string, altPartnerId: string, version: string, companyId: string, direction: string, transactionSet: string): Promise<boolean> {
        const profile = await new ModelEdibillingProfile().searchSingle({
            partner_id: profileId,
            alt_partner_id: altPartnerId,
            version: version,
            company_id: companyId,
            direction: direction,
            transaction_set: transactionSet
        });
        return profile !== null && profile !== undefined;
    }

    setAuditRequiredFields(direction: string) {
        this.textboxAuditIntervalHours.required = direction === this.INBOUND;
        this.textboxAuditIntervalMinutes.required = direction === this.INBOUND;
    }
}
